import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ConversionBlock, PriceTag } from '@/pages/guias/components/GuiaComponents';
import { ServiceBreadcrumb, SchemaService } from '../components/ServicioComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';
import { SchemaPriceOffer } from '@/components/feature/SchemaOrg';

export default function ReparacionToldosMalaga() {
  return (
    <>
      <title>Reparación de toldos en Málaga | Servicio técnico profesional 24/48h</title>
      <meta
        name="description"
        content="Reparación profesional de toldos en Málaga. Reparamos toldos de todas las marcas: brazos, lonas, motores, mecanismos. Respuesta en 24/48h con presupuesto gratis sin compromiso."
      />
      <meta
        name="keywords"
        content="reparacion toldos malaga, reparar toldo malaga, servicio tecnico toldos malaga, arreglar toldo malaga, toldero malaga reparacion"
      />
      <link rel="canonical" href="https://toldos-malaga.es/reparacion-toldos-malaga" />
      <SchemaService
        name="Reparación de toldos en Málaga"
        description="Servicio técnico profesional de reparación de toldos en Málaga y provincia. Reparamos todo tipo de averías: toldos que no recogen, brazos rotos, lonas desgarradas, motores eléctricos, mecanismos desgastados. Respuesta 24/48h y presupuesto gratuito sin compromiso."
        priceRange="desde 50 €"
      />
      <SchemaPriceOffer price={120} name="Reparación completa de toldo en Málaga" description="Reparación de toldo con cambio de piezas y ajuste completo del mecanismo." />
      <meta property="og:title" content="Reparación de toldos en Málaga | Servicio técnico profesional 24/48h | Toldos Málaga" />
      <meta property="og:description" content="Reparación profesional de toldos en Málaga. Reparamos toldos de todas las marcas: brazos, lonas, motores, mecanismos. Respuesta en 24/48h con presupuesto gratis sin compromiso." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/reparacion-toldos-malaga" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Reparación de toldos en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Reparación profesional de toldos en Málaga. Todas las marcas. Respuesta 24/48h. Presupuesto gratis." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />
      <Navbar />
      <main>
        <ServiceBreadcrumb currentTitle="Reparación de toldos en Málaga" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/026-servicio-reparacion-hero.webp"
              alt="Técnico profesional reparando un toldo en Málaga"
              title="Reparación de toldos en Málaga - Servicio técnico profesional"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Servicio técnico
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Reparación de toldos en Málaga
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              En Málaga, un toldo no es un lujo: es una necesidad. Con más de 300 días de sol al año, una terraza sin sombra se vuelve inhabitable en verano. Pero los toldos sufren: el viento de levante, el salitre de la costa, el calor extremo y la calima pasan factura a brazos, lonas y motores. Somos el servicio técnico que necesitas cuando tu toldo dice basta.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Qué tipo de averías reparamos?
            </h2>

            <p>
              No hay dos toldos iguales, pero sí hay averías que se repiten una y otra vez en la costa malagueña. Estas son las que más vemos —y las que más rápido solucionamos—:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-arrow-go-back-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Toldo que no recoge</h3>
                <p className="text-sm text-foreground-600">
                  Muelles fatigados, engranajes desgastados, tensores desajustados. La avería más común en Málaga.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-tools-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Brazos rotos o doblados</h3>
                <p className="text-sm text-foreground-600">
                  Rachas de levante, corrosión por salitre o fatiga del aluminio. Punto recto y articulados.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-plug-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Motores eléctricos</h3>
                <p className="text-sm text-foreground-600">
                  Sustitución de motores, condensadores, finales de carrera y cableado dañado por humedad.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-contrast-drop-line text-primary-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Lonas descolgadas o rotas</h3>
                <p className="text-sm text-foreground-600">
                  Tensado de lonas, reparación de costuras, sustitución completa del tejido con materiales de calidad.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Por qué elegir nuestro servicio de reparación?
            </h2>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-timer-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Respuesta en 24/48 horas.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Sabemos que un toldo estropeado en pleno agosto en Málaga no puede esperar una semana. Nos desplazamos a cualquier punto de la provincia en un máximo de 48 horas tras tu llamada.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-price-tag-3-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Presupuesto gratis, sin compromiso.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Evaluamos la avería, te damos un precio cerrado por escrito y tú decides. Sin sorpresas. Si la reparación no es rentable, te lo decimos con sinceridad.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Todas las marcas, todas las averías.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    No somos un servicio oficial de una sola marca. Reparamos toldos de cualquier fabricante, con cualquier mecanismo, cualquier antigüedad. Si se puede reparar, lo reparamos.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Conocemos el clima de Málaga.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    No es lo mismo reparar un toldo en Madrid que en la Costa del Sol. Sabemos qué piezas sufren más con el salitre, qué lonas aguantan mejor el sol malagueño y cómo prevenir que el levante te rompa el toldo otra vez.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Precios orientativos de reparación
            </h2>

            <p>
              Cada toldo es distinto, así que el precio exacto depende del diagnóstico. Pero para que te hagas una idea, estos son los precios más habituales que manejamos en Málaga:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-200">
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Tipo de reparación</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Precio orientativo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 text-foreground-800">Ajuste de tensores y limpieza de mecanismo</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 50 €</td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 text-foreground-800">Cambio de muelle de brazo</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 70 €</td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 text-foreground-800">Sustitución de brazo completo</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 90 €</td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 text-foreground-800">Cambio de motor eléctrico</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 180 €</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-foreground-800">Sustitución de lona completa</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 120 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="my-8">
              <ConversionBlock
                title="¿Toldo estropeado? Te damos presupuesto hoy"
                subtitle="Cuéntanos qué le pasa a tu toldo y nos desplazamos a tu domicilio en Málaga o provincia. Diagnóstico profesional y presupuesto cerrado sin compromiso."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
              <PriceTag price="desde 50 €" description="Reparación básica" />
              <PriceTag price="desde 120 €" description="Reparación completa" />
              <p className="text-sm text-foreground-500">
                Todos los precios incluyen mano de obra y desplazamiento en Málaga capital. Presupuesto exacto tras diagnóstico.
              </p>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}